import { toast as sonnerToast } from 'sonner';
import type { ReactNode } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'loading';

export type ToastOptions = {
  type?: ToastType;
  message: ReactNode;
  description?: ReactNode;
  duration?: number;
  action?: { label: string; onClick: () => void };
};

// UB25 — durations per type. With an action, sonner extends to its own
// internal default; we explicitly set 10_000 to honour the spec.
const DEFAULT_DURATION: Record<ToastType, number> = {
  success: 3000,
  error: 6000,
  info: 4000,
  loading: Infinity,
};

const BORDER_BY_TYPE: Record<ToastType, string> = {
  success: 'var(--ddm-positive)',
  error: 'var(--ddm-alert)',
  info: 'var(--ddm-warning)',
  loading: 'var(--ddm-bronze)',
};

export function useToast() {
  function show({ type = 'success', message, description, duration, action }: ToastOptions) {
    const fallback = action ? 10_000 : DEFAULT_DURATION[type];
    const finalDuration = duration ?? fallback;

    return sonnerToast(message, {
      description,
      duration: finalDuration === Infinity ? Number.POSITIVE_INFINITY : finalDuration,
      action: action ? { label: action.label, onClick: action.onClick } : undefined,
      style: {
        borderInlineStartWidth: '3px',
        borderInlineStartColor: BORDER_BY_TYPE[type],
      },
    });
  }

  function dismiss(id?: string | number) {
    sonnerToast.dismiss(id);
  }

  return { show, dismiss };
}
